import {CSSProperties, useEffect, useId, useRef, useState} from "react";
import {css} from "@panda/css";

import {ArrowIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IPersonalizedLocationPrice} from "../../types/IPersonalizedHomepage";

const SHOW_MORE_LABEL = "Pokaż więcej";

interface IProps {
    prices: IPersonalizedLocationPrice[];
    /** Latest `YYYY-MM` month carrying data (RP-801 series); falls back to the current month. */
    latestPricedMonth?: string | null;
    isLoading?: boolean;
    hasError?: boolean;
    onShowMoreClick?: (ctaLabel: string) => void;
}

const INITIAL_PRICES_LIMIT_DESKTOP = 4;
const INITIAL_PRICES_LIMIT_MOBILE = 3;
const PRICES_BATCH_SIZE = 4;
const emptyStateIllustration = require("../../images/empty_prices_cat.png");

const PRICES_SKELETON_ROWS = [0, 1, 2];
const priceFormatter = new Intl.NumberFormat("pl-PL", {maximumFractionDigits: 0});

export const PersonalizedHomepagePricesCard = (props: IProps) => {
    const {prices, latestPricedMonth = null, isLoading = false, hasError = false, onShowMoreClick} = props;
    const headingId = useId();
    const isEmpty = !isLoading && !hasError && props.prices.length === 0;
    const {isMobile} = useUserDevice();
    const initialPricesLimit = isMobile ? INITIAL_PRICES_LIMIT_MOBILE : INITIAL_PRICES_LIMIT_DESKTOP;
    const [visibleCount, setVisibleCount] = useState(initialPricesLimit);
    const visiblePrices = prices.slice(0, visibleCount);
    const hasMorePrices = visibleCount < prices.length;
    const initialPricesLimitRef = useRef(initialPricesLimit);
    initialPricesLimitRef.current = initialPricesLimit;

    // Reset only when the data changes — crossing the 1024px breakpoint must not
    // collapse a list the user already expanded.
    useEffect(() => {
        setVisibleCount(initialPricesLimitRef.current);
    }, [prices]);

    return (
        <section className={cardStyle} aria-labelledby={headingId} aria-busy={isLoading}>
            <header className={headStyle}>
                <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                    Ceny w Twoich lokalizacjach
                </Text>
                <Text as="p" variant="info_txt_2" className={subheadingStyle}>
                    {isEmpty ? (
                        "Rozpocznij swoje poszukiwania aby zobaczyć ceny w Twoich lokalizacjach."
                    ) : (
                        <>
                            <span className={subheadingMobileStyle}>Średnia cena za m² · trend 6 mies.</span>
                            <span className={subheadingDesktopStyle}>Średnia cena za m² · trend 6 miesięcy</span>
                        </>
                    )}
                </Text>
            </header>

            <div className={bodyStyle}>
                {isLoading ? (
                    <ul className={listStyle}>
                        {PRICES_SKELETON_ROWS.map((row) => (
                            <li key={row} className={skeletonRowStyle} />
                        ))}
                    </ul>
                ) : null}

                {!isLoading && hasError ? (
                    <p role="status" className={stateStyle}>
                        <Text as="span" variant="body_copy_2">
                            Nie udało się pobrać najnowszych cen. Spróbujemy ponownie przy kolejnej wizycie.
                        </Text>
                    </p>
                ) : null}

                {isEmpty ? (
                    <div className={emptyIllustrationAreaStyle}>
                        <img className={emptyIllustrationStyle} src={emptyStateIllustration} alt="" loading="lazy" />
                    </div>
                ) : null}

                {!isLoading && !hasError && prices.length > 0 ? (
                    <>
                        <ul className={listStyle}>
                            {visiblePrices.map((price) => (
                                <PersonalizedHomepagePriceRow key={price.id} price={price} />
                            ))}
                        </ul>

                        {hasMorePrices ? (
                            <button
                                type="button"
                                className={showMoreStyle}
                                onClick={() => {
                                    setVisibleCount((count) => Math.min(count + PRICES_BATCH_SIZE, prices.length));
                                    onShowMoreClick?.(SHOW_MORE_LABEL);
                                }}
                            >
                                {SHOW_MORE_LABEL}
                            </button>
                        ) : null}
                    </>
                ) : null}
            </div>

            {!isEmpty ? (
                <footer className={footerStyle}>
                    <Text as="span" variant="info_txt_2" className={footerNoteStyle}>
                        Dane: rynekpierwotny.pl · {getDataUpdateLabel(latestPricedMonth)}
                    </Text>

                    <a className={reportLinkStyle} href={rpAppLink.prices.flats.base()}>
                        <Text as="span" variant="body_copy_2">
                            Raport cen
                        </Text>
                        <span className={reportLinkArrowStyle} aria-hidden="true">
                            <ArrowIcon size="1.4" fill="var(--colors-secondary)" viewBox="1 2.8 8 3.9" />
                        </span>
                    </a>
                </footer>
            ) : null}
        </section>
    );
};

const PersonalizedHomepagePriceRow = ({price}: {price: IPersonalizedLocationPrice}) => {
    const isIncrease = price.changePercent >= 0;

    return (
        <li className={rowStyle}>
            <div className={rowLocationStyle}>
                <Text as="span" variant="body_copy_2" className={locationNameStyle}>
                    {price.locationName}
                </Text>
                <Text as="span" variant="info_txt_3" className={locationCaptionStyle}>
                    śr. cena za m²
                </Text>
            </div>

            <PersonalizedHomepagePriceSparkline
                history={price.history}
                historyMonths={price.historyMonths}
                historyPrecedingPrice={price.historyPrecedingPrice}
            />

            <div className={rowPriceStyle}>
                <Text as="span" variant="body_copy_2" className={priceValueStyle}>
                    {priceFormatter.format(price.pricePerSquareMeter)} zł/m²
                </Text>
                <span className={changeStyle}>
                    <span className={isIncrease ? changeArrowUpStyle : changeArrowDownStyle} aria-hidden="true" />
                    <Text as="span" variant="info_txt_2" className={isIncrease ? changeIncreaseStyle : changeDecreaseStyle}>
                        {isIncrease ? "+" : ""}
                        {price.changePercent}%
                    </Text>
                </span>
            </div>
        </li>
    );
};

/** "2026-04" → "Kwiecień 2026"; built from numeric parts so no timezone shifts the month. */
const getSparklineMonthLabel = (month: string): string => {
    const [year, monthNumber] = month.split("-").map(Number);
    const monthName = new Intl.DateTimeFormat("pl-PL", {month: "long"}).format(new Date(year, monthNumber - 1, 1));

    return `${monthName.charAt(0).toLocaleUpperCase("pl-PL")}${monthName.slice(1)} ${year}`;
};

/** Each bar compares against the previous month — rises and falls are colored independently. */
const PersonalizedHomepagePriceSparkline = (props: {history: number[]; historyMonths: string[]; historyPrecedingPrice: number | null}) => {
    const {history, historyMonths, historyPrecedingPrice} = props;
    const lowestPrice = Math.min(...history);
    const highestPrice = Math.max(...history);
    const priceRange = highestPrice - lowestPrice;

    return (
        <span className={sparklineStyle} aria-hidden="true">
            {history.map((price, index) => {
                const previousMonthPrice = index > 0 ? history[index - 1] : historyPrecedingPrice;
                const isMonthlyIncrease = previousMonthPrice !== null && price > previousMonthPrice;

                return (
                    <span key={`${price}-${index}`} className={sparklineBarHolderStyle}>
                        <span
                            className={isMonthlyIncrease ? sparklineBarIncreaseStyle : sparklineBarDecreaseStyle}
                            style={{"--sparkline-bar-height": `${priceRange === 0 ? 100 : 40 + ((price - lowestPrice) / priceRange) * 60}%`} as CSSProperties}
                        />
                        <span className={sparklineTooltipStyle} data-sparkline-tooltip>
                            {historyMonths[index] ? <span className={sparklineTooltipMonthStyle}>{getSparklineMonthLabel(historyMonths[index])}</span> : null}
                            <span className={sparklineTooltipPriceStyle}>{priceFormatter.format(price)} zł/m²</span>
                        </span>
                    </span>
                );
            })}
        </span>
    );
};

/**
 * Formats the last priced `YYYY-MM` month; without data it falls back to the
 * current month. Built from numeric parts — parsing "YYYY-MM-01" would mean
 * UTC midnight and shift the label a month back for viewers west of UTC.
 */
const getDataUpdateLabel = (latestPricedMonth: string | null): string => {
    const [year, month] = (latestPricedMonth ?? "").split("-").map(Number);
    const date = year && month >= 1 && month <= 12 ? new Date(year, month - 1, 1) : new Date();

    return date.toLocaleDateString("pl-PL", {month: "short", year: "numeric"});
};

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,

    md: {
        minHeight: "41.1rem",
        padding: 3
    }
});

const headStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem"
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const subheadingStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

const subheadingMobileStyle = css({
    md: {
        display: "none"
    }
});

const subheadingDesktopStyle = css({
    display: "none",

    md: {
        display: "inline"
    }
});

const bodyStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    alignItems: "flex-start",
    gap: 2,
    marginTop: 2,

    md: {
        marginTop: 2.5
    }
});

const listStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
    margin: 0,
    padding: 0,
    listStyle: "none"
});

const skeletonRowStyle = css({
    height: "4.4rem",
    backgroundColor: "var(--colors-gray-200)",
    borderRadius: 1
});

const stateStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

const rowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2
});

const rowLocationStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    gap: "0.2rem",
    minWidth: 0
});

const locationNameStyle = css({
    overflow: "hidden",
    color: "var(--colors-secondary)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const locationCaptionStyle = css({
    color: "var(--colors-gray-600)"
});

const sparklineStyle = css({
    display: "flex",
    flex: "0 0 auto",
    alignItems: "flex-end",
    gap: "2px",
    width: "6rem",
    height: "2.8rem",

    md: {
        gap: "3px",
        width: "11.8rem",
        height: "3.4rem"
    }
});

const sparklineBarHolderStyle = css({
    position: "relative",
    display: "flex",
    flex: "1 1 0",
    alignItems: "flex-end",
    minWidth: "4px",
    height: "100%",

    _hover: {
        "& [data-sparkline-tooltip]": {
            opacity: 1,
            visibility: "visible"
        }
    }
});

const sparklineTooltipStyle = css({
    position: "absolute",
    bottom: "calc(100% + 0.6rem)",
    left: "50%",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    px: 2,
    py: 1,
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    borderRadius: "999px",
    boxShadow: "0 4px 10px 0 rgba(35, 35, 45, 0.08)",
    opacity: 0,
    pointerEvents: "none",
    transform: "translateX(-50%)",
    transition: "opacity 0.15s ease",
    visibility: "hidden",
    whiteSpace: "nowrap"
});

const sparklineTooltipMonthStyle = css({
    fontSize: "1rem",
    lineHeight: "1.2rem"
});

const sparklineTooltipPriceStyle = css({
    fontSize: "1.2rem",
    fontWeight: 700,
    lineHeight: "1.6rem"
});

const sparklineBarStyle = {
    width: "100%",
    height: "var(--sparkline-bar-height, 100%)",
    borderRadius: "2px"
} as const;

const sparklineBarIncreaseStyle = css({
    ...sparklineBarStyle,
    backgroundColor: "#f0b3aa"
});

const sparklineBarDecreaseStyle = css({
    ...sparklineBarStyle,
    backgroundColor: "#b9e19b"
});

const rowPriceStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "0 0 auto",
    alignItems: "flex-end",
    gap: 0.5,
    width: "8.4rem",

    md: {
        width: "9.6rem"
    }
});

const priceValueStyle = css({
    color: "var(--colors-secondary)",
    whiteSpace: "nowrap"
});

const changeStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    padding: 0.5
});

const changeArrowStyle = {
    width: 0,
    height: 0,
    borderRight: "6px solid transparent",
    borderLeft: "6px solid transparent"
} as const;

const changeArrowUpStyle = css({
    ...changeArrowStyle,
    borderBottom: "9px solid #b30021"
});

const changeArrowDownStyle = css({
    ...changeArrowStyle,
    borderTop: "9px solid #026429"
});

const changeIncreaseStyle = css({
    color: "#b30021"
});

const changeDecreaseStyle = css({
    color: "#026429"
});

const showMoreStyle = css({
    padding: 0,
    color: "var(--colors-secondary)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "1.4rem",
    lineHeight: "2rem",
    textDecoration: "underline"
});

const footerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    marginTop: 2,
    pt: 2,
    borderTop: "1px solid var(--colors-gray-300)"
});

const footerNoteStyle = css({
    color: "var(--colors-gray-600)"
});

const reportLinkStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    color: "var(--colors-secondary)",
    textDecoration: "none",

    _hover: {
        color: "var(--colors-secondary)",
        textDecoration: "underline"
    }
});

/** ArrowIcon points left and sits in a sparse 10×10 viewBox; crop + rotate to match Figma's 14px right arrow. */
const reportLinkArrowStyle = css({
    display: "inline-flex",
    transform: "rotate(180deg)"
});

const emptyIllustrationAreaStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: 2
});

const emptyIllustrationStyle = css({
    width: "100%",
    maxWidth: "44.3rem",
    height: "auto"
});
import {CSSProperties, useEffect, useId, useRef, useState} from "react";
import {css} from "@panda/css";

import {ArrowIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {IPersonalizedLocationPrice} from "../../types/IPersonalizedHomepage";

const SHOW_MORE_LABEL = "Pokaż więcej";

interface IProps {
    prices: IPersonalizedLocationPrice[];
    /** Latest `YYYY-MM` month carrying data (RP-801 series); falls back to the current month. */
    latestPricedMonth?: string | null;
    isLoading?: boolean;
    hasError?: boolean;
    onShowMoreClick?: (ctaLabel: string) => void;
}

const INITIAL_PRICES_LIMIT_DESKTOP = 4;
const INITIAL_PRICES_LIMIT_MOBILE = 3;
const PRICES_BATCH_SIZE = 4;
const emptyStateIllustration = require("../../images/empty_prices_cat.png");

const PRICES_SKELETON_ROWS = [0, 1, 2];
const priceFormatter = new Intl.NumberFormat("pl-PL", {maximumFractionDigits: 0});

export const PersonalizedHomepagePricesCard = (props: IProps) => {
    const {prices, latestPricedMonth = null, isLoading = false, hasError = false, onShowMoreClick} = props;
    const headingId = useId();
    const isEmpty = !isLoading && !hasError && props.prices.length === 0;
    const {isMobile} = useUserDevice();
    const initialPricesLimit = isMobile ? INITIAL_PRICES_LIMIT_MOBILE : INITIAL_PRICES_LIMIT_DESKTOP;
    const [visibleCount, setVisibleCount] = useState(initialPricesLimit);
    const visiblePrices = prices.slice(0, visibleCount);
    const hasMorePrices = visibleCount < prices.length;
    const initialPricesLimitRef = useRef(initialPricesLimit);
    initialPricesLimitRef.current = initialPricesLimit;

    // Reset only when the data changes — crossing the 1024px breakpoint must not
    // collapse a list the user already expanded.
    useEffect(() => {
        setVisibleCount(initialPricesLimitRef.current);
    }, [prices]);

    return (
        <section className={cardStyle} aria-labelledby={headingId} aria-busy={isLoading}>
            <header className={headStyle}>
                <Text id={headingId} as="h2" variant="headline_6" className={headingStyle}>
                    Ceny w Twoich lokalizacjach
                </Text>
                <Text as="p" variant="info_txt_2" className={subheadingStyle}>
                    {isEmpty ? (
                        "Rozpocznij swoje poszukiwania aby zobaczyć ceny w Twoich lokalizacjach."
                    ) : (
                        <>
                            <span className={subheadingMobileStyle}>Średnia cena za m² · trend 6 mies.</span>
                            <span className={subheadingDesktopStyle}>Średnia cena za m² · trend 6 miesięcy</span>
                        </>
                    )}
                </Text>
            </header>

            <div className={bodyStyle}>
                {isLoading ? (
                    <ul className={listStyle}>
                        {PRICES_SKELETON_ROWS.map((row) => (
                            <li key={row} className={skeletonRowStyle} />
                        ))}
                    </ul>
                ) : null}

                {!isLoading && hasError ? (
                    <p role="status" className={stateStyle}>
                        <Text as="span" variant="body_copy_2">
                            Nie udało się pobrać najnowszych cen. Spróbujemy ponownie przy kolejnej wizycie.
                        </Text>
                    </p>
                ) : null}

                {isEmpty ? (
                    <div className={emptyIllustrationAreaStyle}>
                        <img className={emptyIllustrationStyle} src={emptyStateIllustration} alt="" loading="lazy" />
                    </div>
                ) : null}

                {!isLoading && !hasError && prices.length > 0 ? (
                    <>
                        <ul className={listStyle}>
                            {visiblePrices.map((price) => (
                                <PersonalizedHomepagePriceRow key={price.id} price={price} />
                            ))}
                        </ul>

                        {hasMorePrices ? (
                            <button
                                type="button"
                                className={showMoreStyle}
                                onClick={() => {
                                    setVisibleCount((count) => Math.min(count + PRICES_BATCH_SIZE, prices.length));
                                    onShowMoreClick?.(SHOW_MORE_LABEL);
                                }}
                            >
                                {SHOW_MORE_LABEL}
                            </button>
                        ) : null}
                    </>
                ) : null}
            </div>

            {!isEmpty ? (
                <footer className={footerStyle}>
                    <Text as="span" variant="info_txt_2" className={footerNoteStyle}>
                        Dane: rynekpierwotny.pl · {getDataUpdateLabel(latestPricedMonth)}
                    </Text>

                    <a className={reportLinkStyle} href={rpAppLink.prices.flats.base()}>
                        <Text as="span" variant="body_copy_2">
                            Raport cen
                        </Text>
                        <span className={reportLinkArrowStyle} aria-hidden="true">
                            <ArrowIcon size="1.4" fill="var(--colors-secondary)" viewBox="1 2.8 8 3.9" />
                        </span>
                    </a>
                </footer>
            ) : null}
        </section>
    );
};

const PersonalizedHomepagePriceRow = ({price}: {price: IPersonalizedLocationPrice}) => {
    const isIncrease = price.changePercent >= 0;

    return (
        <li className={rowStyle}>
            <div className={rowLocationStyle}>
                <Text as="span" variant="body_copy_2" className={locationNameStyle}>
                    {price.locationName}
                </Text>
                <Text as="span" variant="info_txt_3" className={locationCaptionStyle}>
                    śr. cena za m²
                </Text>
            </div>

            <PersonalizedHomepagePriceSparkline
                history={price.history}
                historyMonths={price.historyMonths}
                historyPrecedingPrice={price.historyPrecedingPrice}
            />

            <div className={rowPriceStyle}>
                <Text as="span" variant="body_copy_2" className={priceValueStyle}>
                    {priceFormatter.format(price.pricePerSquareMeter)} zł/m²
                </Text>
                <span className={changeStyle}>
                    <span className={isIncrease ? changeArrowUpStyle : changeArrowDownStyle} aria-hidden="true" />
                    <Text as="span" variant="info_txt_2" className={isIncrease ? changeIncreaseStyle : changeDecreaseStyle}>
                        {isIncrease ? "+" : ""}
                        {price.changePercent}%
                    </Text>
                </span>
            </div>
        </li>
    );
};

/** "2026-04" → "Kwiecień 2026"; built from numeric parts so no timezone shifts the month. */
const getSparklineMonthLabel = (month: string): string => {
    const [year, monthNumber] = month.split("-").map(Number);
    const monthName = new Intl.DateTimeFormat("pl-PL", {month: "long"}).format(new Date(year, monthNumber - 1, 1));

    return `${monthName.charAt(0).toLocaleUpperCase("pl-PL")}${monthName.slice(1)} ${year}`;
};

/** Each bar compares against the previous month — rises and falls are colored independently. */
const PersonalizedHomepagePriceSparkline = (props: {history: number[]; historyMonths: string[]; historyPrecedingPrice: number | null}) => {
    const {history, historyMonths, historyPrecedingPrice} = props;
    const lowestPrice = Math.min(...history);
    const highestPrice = Math.max(...history);
    const priceRange = highestPrice - lowestPrice;

    return (
        <span className={sparklineStyle} aria-hidden="true">
            {history.map((price, index) => {
                const previousMonthPrice = index > 0 ? history[index - 1] : historyPrecedingPrice;
                const isMonthlyIncrease = previousMonthPrice !== null && price > previousMonthPrice;

                return (
                    <span key={`${price}-${index}`} className={sparklineBarHolderStyle}>
                        <span
                            className={isMonthlyIncrease ? sparklineBarIncreaseStyle : sparklineBarDecreaseStyle}
                            style={{"--sparkline-bar-height": `${priceRange === 0 ? 100 : 40 + ((price - lowestPrice) / priceRange) * 60}%`} as CSSProperties}
                        />
                        <span className={sparklineTooltipStyle} data-sparkline-tooltip>
                            {historyMonths[index] ? <span className={sparklineTooltipMonthStyle}>{getSparklineMonthLabel(historyMonths[index])}</span> : null}
                            <span className={sparklineTooltipPriceStyle}>{priceFormatter.format(price)} zł/m²</span>
                        </span>
                    </span>
                );
            })}
        </span>
    );
};

/**
 * Formats the last priced `YYYY-MM` month; without data it falls back to the
 * current month. Built from numeric parts — parsing "YYYY-MM-01" would mean
 * UTC midnight and shift the label a month back for viewers west of UTC.
 */
const getDataUpdateLabel = (latestPricedMonth: string | null): string => {
    const [year, month] = (latestPricedMonth ?? "").split("-").map(Number);
    const date = year && month >= 1 && month <= 12 ? new Date(year, month - 1, 1) : new Date();

    return date.toLocaleDateString("pl-PL", {month: "short", year: "numeric"});
};

const cardStyle = css({
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    height: "100%",
    padding: 2,
    backgroundColor: "var(--colors-white)",
    borderRadius: 2,

    md: {
        minHeight: "41.1rem",
        padding: 3
    }
});

const headStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: "0.2rem"
});

const headingStyle = css({
    margin: 0,
    color: "var(--colors-secondary)"
});

const subheadingStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

const subheadingMobileStyle = css({
    md: {
        display: "none"
    }
});

const subheadingDesktopStyle = css({
    display: "none",

    md: {
        display: "inline"
    }
});

const bodyStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    alignItems: "flex-start",
    gap: 2,
    marginTop: 2,

    md: {
        marginTop: 2.5
    }
});

const listStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    width: "100%",
    margin: 0,
    padding: 0,
    listStyle: "none"
});

const skeletonRowStyle = css({
    height: "4.4rem",
    backgroundColor: "var(--colors-gray-200)",
    borderRadius: 1
});

const stateStyle = css({
    margin: 0,
    color: "var(--colors-gray-700)"
});

const rowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2
});

const rowLocationStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    gap: "0.2rem",
    minWidth: 0
});

const locationNameStyle = css({
    overflow: "hidden",
    color: "var(--colors-secondary)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const locationCaptionStyle = css({
    color: "var(--colors-gray-600)"
});

const sparklineStyle = css({
    display: "flex",
    flex: "0 0 auto",
    alignItems: "flex-end",
    gap: "2px",
    width: "6rem",
    height: "2.8rem",

    md: {
        gap: "3px",
        width: "11.8rem",
        height: "3.4rem"
    }
});

const sparklineBarHolderStyle = css({
    position: "relative",
    display: "flex",
    flex: "1 1 0",
    alignItems: "flex-end",
    minWidth: "4px",
    height: "100%",

    _hover: {
        "& [data-sparkline-tooltip]": {
            opacity: 1,
            visibility: "visible"
        }
    }
});

const sparklineTooltipStyle = css({
    position: "absolute",
    bottom: "calc(100% + 0.6rem)",
    left: "50%",
    zIndex: 2,
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    px: 2,
    py: 1,
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-white)",
    borderRadius: "999px",
    boxShadow: "0 4px 10px 0 rgba(35, 35, 45, 0.08)",
    opacity: 0,
    pointerEvents: "none",
    transform: "translateX(-50%)",
    transition: "opacity 0.15s ease",
    visibility: "hidden",
    whiteSpace: "nowrap"
});

const sparklineTooltipMonthStyle = css({
    fontSize: "1rem",
    lineHeight: "1.2rem"
});

const sparklineTooltipPriceStyle = css({
    fontSize: "1.2rem",
    fontWeight: 700,
    lineHeight: "1.6rem"
});

const sparklineBarStyle = {
    width: "100%",
    height: "var(--sparkline-bar-height, 100%)",
    borderRadius: "2px"
} as const;

const sparklineBarIncreaseStyle = css({
    ...sparklineBarStyle,
    backgroundColor: "#f0b3aa"
});

const sparklineBarDecreaseStyle = css({
    ...sparklineBarStyle,
    backgroundColor: "#b9e19b"
});

const rowPriceStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "0 0 auto",
    alignItems: "flex-end",
    gap: 0.5,
    width: "8.4rem",

    md: {
        width: "9.6rem"
    }
});

const priceValueStyle = css({
    color: "var(--colors-secondary)",
    whiteSpace: "nowrap"
});

const changeStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    padding: 0.5
});

const changeArrowStyle = {
    width: 0,
    height: 0,
    borderRight: "6px solid transparent",
    borderLeft: "6px solid transparent"
} as const;

const changeArrowUpStyle = css({
    ...changeArrowStyle,
    borderBottom: "9px solid #b30021"
});

const changeArrowDownStyle = css({
    ...changeArrowStyle,
    borderTop: "9px solid #026429"
});

const changeIncreaseStyle = css({
    color: "#b30021"
});

const changeDecreaseStyle = css({
    color: "#026429"
});

const showMoreStyle = css({
    padding: 0,
    color: "var(--colors-secondary)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "1.4rem",
    lineHeight: "2rem",
    textDecoration: "underline"
});

const footerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    marginTop: 2,
    pt: 2,
    borderTop: "1px solid var(--colors-gray-300)"
});

const footerNoteStyle = css({
    color: "var(--colors-gray-600)"
});

const reportLinkStyle = css({
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    color: "var(--colors-secondary)",
    textDecoration: "none",

    _hover: {
        color: "var(--colors-secondary)",
        textDecoration: "underline"
    }
});

/** ArrowIcon points left and sits in a sparse 10×10 viewBox; crop + rotate to match Figma's 14px right arrow. */
const reportLinkArrowStyle = css({
    display: "inline-flex",
    transform: "rotate(180deg)"
});

const emptyIllustrationAreaStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: 2
});

const emptyIllustrationStyle = css({
    width: "100%",
    maxWidth: "44.3rem",
    height: "auto"
});
